import React, { useEffect, useRef, useState } from 'react';
import { Users, Heart, Shield, Globe } from 'lucide-react';

const AboutSection = () => {
  const sectionRef = useRef(null);

  const values = [
    {
      icon: Heart,
      title: 'Dignity First',
      description: 'Every interaction honors the person, not the condition. We celebrate life stories and present moments.'
    },
    {
      icon: Users,
      title: 'Human Connection',
      description: 'Real conversations between real people. Technology facilitates, but humanity leads.'
    },
    {
      icon: Shield,
      title: 'Safe & Monitored',
      description: 'Privacy-protected, caregiver-approved sessions with AI support and trained volunteers.'
    },
    {
      icon: Globe,
      title: 'Inclusive & Multilingual',
      description: 'Breaking language barriers with AI assistance in multiple languages, ensuring everyone feels heard.'
    }
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-24 relative overflow-hidden"
      style={{ background: '#FFFFFF' }}
    >
      {/* Decorative background elements - Blue themed */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #1E56A0 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-5" style={{ background: 'radial-gradient(circle, #D6E4F0 0%, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ fontFamily: 'Merriweather, serif', color: '#163172' }}
          >
            What is Sage And Seed?
          </h2>
          <p
            className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed"
            style={{ color: '#000000' }}
          >
            We're more than a platform. We're a movement to restore connection, purpose, and joy across generations.
            Seniors living with dementia gain companionship and engagement. Young adults gain perspective, empathy,
            and the profound gift of being truly needed.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div
                key={index}
                className="p-8 rounded-2xl hover:shadow-xl border border-[rgba(30,86,160,0.1)]"
                style={{
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F6F6F6 100%)',
                }}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 shadow-sm"
                  style={{ background: 'linear-gradient(135deg, #D6E4F0 0%, #F6F6F6 100%)' }}
                >
                  <Icon className="w-7 h-7" style={{ color: '#1E56A0' }} />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#163172' }}>
                  {value.title}
                </h3>
                <p className="leading-relaxed" style={{ color: '#000000' }}>
                  {value.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Why it matters */}
        <div
          className="mt-16 p-10 rounded-3xl"
          style={{
            background: 'linear-gradient(135deg, rgba(214, 228, 240, 0.3) 0%, rgba(246, 246, 246, 0.5) 100%)',
            border: '1px solid rgba(30, 86, 160, 0.1)'
          }}
        >
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif', color: '#163172' }}>
              Why This Matters
            </h3>
            <p className="text-lg leading-relaxed mb-4" style={{ color: '#000000' }}>
              Social isolation in dementia accelerates cognitive decline and diminishes quality of life. Yet meaningful human
              connection—even brief, simple conversations—can improve mood, reduce anxiety, and restore a sense of self.
            </p>
            <p className="text-lg leading-relaxed" style={{ color: '#000000' }}>
              For young adults, these connections build emotional intelligence, patience, and purpose in ways no classroom can teach.
              <span className="font-semibold" style={{ color: '#1E56A0' }}> Everyone grows. Everyone matters.</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
